import type { CSSProperties } from "react";

import type { GanttLayout, GanttLayoutPreset, GanttSpan, PillAlign } from "./types-gantt";

/**
 * Presets com os valores atuais. Divergência registrada (Catálogo D §4):
 * - `compact` (Profissionais e Local, `gantt.tsx`/`gantt-local.tsx`): faixa 1,625; mês 6 no desktop e 3,8125 no mobile.
 * - `comfortable` (Fornecedor por Obra, `carga-fornecedor-gantt.tsx`): faixa 2,9; mês 2,75 no desktop e 3,8125 no mobile.
 * - Fora dos presets: Obras e Local por Obra usam mês mobile de 2,729; o Fornecedor em segmentos usa barra de 1,25,
 *   respiro de 0,3 entre faixas e padding de 0,6; faixa com conflito ignorado usa 2,5.
 */
export const GANTT_LAYOUT_PRESETS: Record<GanttLayoutPreset, GanttLayout> = {
  compact: {
    laneHeightRem: 1.625,
    rowPaddingRem: 0.5,
    barHeightRem: 1.375,
    monthWidthRem: 6,
    monthWidthMobileRem: 3.8125,
  },
  comfortable: {
    laneHeightRem: 2.9,
    rowPaddingRem: 0.5,
    barHeightRem: 1.25,
    monthWidthRem: 2.75,
    monthWidthMobileRem: 3.8125,
  },
};

/** Aceita um preset ou medidas próprias (parciais, sobre o `compact`). */
export function resolveGanttLayout(
  layout: GanttLayoutPreset | Partial<GanttLayout> = "compact",
): GanttLayout {
  return typeof layout === "string"
    ? GANTT_LAYOUT_PRESETS[layout]
    : { ...GANTT_LAYOUT_PRESETS.compact, ...layout };
}

const round = (value: number) => Number(value.toFixed(4));

/** Distância do topo da linha até a faixa `lane` (base 0), em rem. */
export function laneTopRem(layout: GanttLayout, lane: number): number {
  return round(layout.rowPaddingRem + lane * layout.laneHeightRem);
}

/** `left` e `width` em %, limitados a 0–100 e com largura nunca negativa. */
export function spanToPercent({ start, end }: GanttSpan): { left: number; width: number } {
  const left = Math.min(Math.max(start, 0), 100);
  const right = Math.min(Math.max(end, left), 100);
  return { left: round(left), width: round(right - left) };
}

/** Variáveis internas do layout, definidas inline e lidas pelo SCSS (valor numérico livre; não são ponto de sobrescrita). */
export function layoutVars(layout: GanttLayout): CSSProperties {
  return {
    "--gantt-lane-height": `${layout.laneHeightRem}rem`,
    "--gantt-bar-height": `${layout.barHeightRem}rem`,
    "--gantt-month-width": `${layout.monthWidthRem}rem`,
    "--gantt-month-width-mobile": `${layout.monthWidthMobileRem}rem`,
  } as CSSProperties;
}

/** Posição inline de uma marca: `left`/`width` em %, `top` da faixa e as variáveis do layout. */
export function positionStyle(layout: GanttLayout, span: GanttSpan, lane = 0): CSSProperties {
  const { left, width } = spanToPercent(span);
  return {
    ...layoutVars(layout),
    left: `${left}%`,
    width: `${width}%`,
    top: `${laneTopRem(layout, lane)}rem`,
  };
}

/** Margem (em %) até a borda da linha do tempo em que o pill alinha à borda, em vez de centralizar (Fornecedor). */
const PILL_EDGE_PERCENT = 0.5;

/** Alinhamento do pill na faixa: centralizado, ou colado à borda quando a faixa encosta nela. */
export function pillAlign(span: GanttSpan): PillAlign {
  const { left, width } = spanToPercent(span);
  if (left <= PILL_EDGE_PERCENT) return "start";
  if (left + width >= 100 - PILL_EDGE_PERCENT) return "end";
  return "center";
}
